// Shell view-model – odvozený stav pro prezentační vrstvu shellu

import type { NavigationState } from '../core/navigationState';
import type { LayerDescriptor } from '../core/layerDescriptor';
import type { BreadcrumbEntry, LayoutMode } from '../core/navigationTypes';
import type { AgendaRegistry } from '../core/agendaRegistry';

/** Render target jedné vrstvy v shell projekci */
export type LayerRenderTarget = 'root-content' | 'list-pane' | 'detail-panel' | 'overlay-window';

/** Projekce jedné vrstvy pro shell rendering */
export interface LayerProjection {
  readonly layer: LayerDescriptor;
  readonly renderTarget: LayerRenderTarget;
  readonly title: string;
}

/** Projekce detailového panelu pro list-detail režim */
export interface DetailPanelProjection {
  readonly layer: LayerDescriptor;
  readonly title: string;
}

/** Projekce overlay okna */
export interface WindowProjection {
  readonly layer: LayerDescriptor;
  readonly title: string;
  /** Index v overlay stacku (0 = první overlay) – pro odsazení */
  readonly stackIndex: number;
}

/** View-model pro navigační shell */
export interface NavigationShellViewModel {
  /** Poslední root vrstva – základní obsah */
  readonly activeRoot: LayerDescriptor | null;
  /** Modální vrstvy za aktivním rootem */
  readonly activeModals: readonly LayerDescriptor[];
  /** Vrchní interaktivní vrstva */
  readonly activeTop: LayerDescriptor | null;
  /** Breadcrumb navigační cesta */
  readonly breadcrumbs: readonly BreadcrumbEntry[];
  /** Má aktivní modály */
  readonly hasModals: boolean;
  /** Detail panel pro list-detail režim (null pokud overlay nebo bez detailu) */
  readonly detailPanel: DetailPanelProjection | null;
  /** Overlay window vrstvy (vrstvy, které se renderují jako modální okna přes celý viewport) */
  readonly overlayWindows: readonly WindowProjection[];
  /** Layout policy aktivního rootu (undefined pokud root nemá shell hint nebo není root) */
  readonly layoutMode: LayoutMode | undefined;
}

/**
 * Odvodí shell view-model z navigačního stavu.
 * Shell neparsuje URL ani nepočítá stack sám.
 */
export function deriveShellViewModel(
  state: NavigationState,
  registry?: AgendaRegistry
): NavigationShellViewModel {
  const { layers, activeTopLayerId } = state;

  const activeTop = layers.find((l) => l.layerId === activeTopLayerId) ?? null;

  // Najdi první root (viewport root) a poslední root (potenciální overlay root)
  let firstRootIndex = -1;
  let lastRootIndex = -1;
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    if (layer && layer.presentation === 'root') {
      if (firstRootIndex === -1) firstRootIndex = i;
      lastRootIndex = i;
    }
  }

  const hasOverlayRoot = firstRootIndex !== lastRootIndex && firstRootIndex >= 0;

  // activeRoot je první root – ten definuje viewport a layout policy
  const activeRoot = firstRootIndex >= 0 ? (layers[firstRootIndex] ?? null) : null;

  // Modály mezi prvním rootem a overlay rootem (nebo do konce, pokud overlay root neexistuje)
  // Pokud žádný root neexistuje, všechny vrstvy jsou "rootless" modály
  const modalEndIndex = hasOverlayRoot ? lastRootIndex : layers.length;
  const activeModals =
    firstRootIndex >= 0
      ? layers.slice(firstRootIndex + 1, modalEndIndex)
      : layers.slice(0, modalEndIndex);

  // Resolve root layout policy z prvního rootu
  const rootConfig = activeRoot && registry ? registry.getById(activeRoot.agendaId) : undefined;
  const layoutMode = rootConfig?.presentationHints?.shell?.layoutMode;
  const isListDetail = layoutMode === 'list-detail';

  // Detail panel drží PRVNÍ modal se `zone: 'detail'` (razítkuje ho `openDetail` = výběr v listu).
  // Všechno ostatní jsou overlay okna nad celou agendou.
  let detailPanel: DetailPanelProjection | null = null;
  const overlayWindows: WindowProjection[] = [];

  for (const modal of activeModals) {
    const config = registry?.getById(modal.agendaId);
    if (isListDetail && !detailPanel && modal.zone === 'detail') {
      detailPanel = { layer: modal, title: resolveTitle(modal, config) };
      continue;
    }
    overlayWindows.push({
      layer: modal,
      title: resolveTitle(modal, config),
      stackIndex: overlayWindows.length,
    });
  }

  // Overlay root a jeho modály → všechny jako overlay windows
  if (hasOverlayRoot) {
    const overlayRootAndModals = layers.slice(lastRootIndex);
    for (const layer of overlayRootAndModals) {
      const config = registry?.getById(layer.agendaId);
      overlayWindows.push({
        layer,
        title: resolveTitle(layer, config),
        stackIndex: overlayWindows.length,
      });
    }
  }

  // Breadcrumbs – celá cesta od prvního rootu přes všechny vrstvy
  // Pokud root neexistuje, breadcrumbs zahrnují všechny vrstvy
  const breadcrumbs: BreadcrumbEntry[] = [];
  const visibleLayers = firstRootIndex >= 0 ? layers.slice(firstRootIndex) : layers;

  for (const layer of visibleLayers) {
    const config = registry?.getById(layer.agendaId);
    const title = resolveTitle(layer, config);

    breadcrumbs.push({
      layerId: layer.layerId,
      agendaId: layer.agendaId,
      title,
      presentation: layer.presentation,
      icon: config?.icon,
    });
  }

  // hasModals zahrnuje modály prvního rootu + overlay root vrstvy
  const totalModals = activeModals.length + (hasOverlayRoot ? layers.length - lastRootIndex : 0);

  return {
    activeRoot,
    activeModals,
    activeTop,
    breadcrumbs,
    hasModals: totalModals > 0,
    detailPanel,
    overlayWindows,
    layoutMode,
  };
}

export function resolveTitle(
  layer: LayerDescriptor,
  config?: { title?: string | ((params: Record<string, unknown>) => string) }
): string {
  if (!config?.title) return layer.agendaId;
  if (typeof config.title === 'string') return config.title;
  return config.title(layer.openParams);
}
